import React, { useState } from 'react';
import { FIRELINE_MEDIC_BADGE } from '../brand/firelineMedicBadge';
import RadioReference from '../radio/RadioReference';
import SacCountyProtocols from '../protocols/SacCountyProtocols';
import FireWeather from '../weather/FireWeather';
import DrugCalculator from '../calculator/DrugCalculator';
import SavedIaps from '../iap/SavedIaps';
import Ics214 from '../ics214/Ics214';
import './offline-member-home.css';

export default function OfflineMemberHome({ profile }) {
  const [reference, setReference] = useState('');

  if (reference === 'radio') {
    return (
      <div className="offline-home-root">
        <main className="offline-home-shell">
          <button type="button" className="offline-home-back" onClick={() => setReference('')}>← Offline Home</button>
          <section className="offline-home-card">
            <span className="offline-home-kicker">FIELD REFERENCE · OFFLINE</span>
            <h1>Radio Reference</h1>
            <p>The configured radio bank/channel reference is stored with the app so it remains available without cell service.</p>
            <RadioReference offline />
          </section>
        </main>
      </div>
    );
  }

  if (reference === 'protocols') {
    return (
      <div className="offline-home-root">
        <main className="offline-home-shell">
          <button type="button" className="offline-home-back" onClick={() => setReference('')}>← Offline Home</button>
          <section className="offline-home-card">
            <span className="offline-home-kicker">FIELD REFERENCE · OFFLINE</span>
            <h1>EMS Protocols</h1>
            <p>Search the locally stored EMS policies, procedures, and protocols configured for this deployment.</p>
            <SacCountyProtocols offline />
          </section>
        </main>
      </div>
    );
  }

  if (reference === 'drug-calculator') {
    return (
      <div className="offline-home-root">
        <main className="offline-home-shell">
          <button type="button" className="offline-home-back" onClick={() => setReference('')}>← Offline Home</button>
          <section className="offline-home-card">
            <span className="offline-home-kicker">MEDICATION CALCULATOR · OFFLINE</span>
            <h1>Drug Calculator</h1>
            <p>Configured weight-based dosing, draw volumes, and preparation references remain available without network access.</p>
            <DrugCalculator onOpenProtocols={() => setReference('protocols')} />
          </section>
        </main>
      </div>
    );
  }

  if (reference === 'fire-weather') {
    return (
      <div className="offline-home-root">
        <main className="offline-home-shell">
          <button type="button" className="offline-home-back" onClick={() => setReference('')}>← Offline Home</button>
          <section className="offline-home-card">
            <span className="offline-home-kicker">FIRE WEATHER · SAVED FORECAST</span>
            <h1>Fire Weather</h1>
            <p>View the last National Weather Service fire-weather forecast saved on this device.</p>
            <FireWeather offline />
          </section>
        </main>
      </div>
    );
  }

  if (reference === 'ics214') {
    return (
      <div className="offline-home-root">
        <main className="offline-home-shell">
          <button type="button" className="offline-home-back" onClick={() => setReference('')}>← Offline Home</button>
          <section className="offline-home-card">
            <span className="offline-home-kicker">FIELD DOCUMENTATION · OFFLINE</span>
            <h1>ICS 214 Activity Log</h1>
            <p>Continue saved incident logs, add or edit activities, and generate the PDF without service. Email requires service.</p>
            <Ics214 profile={profile} />
          </section>
        </main>
      </div>
    );
  }

  if (reference === 'iaps') {
    return (
      <div className="offline-home-root">
        <main className="offline-home-shell">
          <button type="button" className="offline-home-back" onClick={() => setReference('')}>← Offline Home</button>
          <section className="offline-home-card">
            <span className="offline-home-kicker">SAVED IAPS · OFFLINE</span>
            <h1>Saved IAPs</h1>
            <p>Open and search incident IAP PDFs already saved on this device. Scanning or browsing a new source requires service.</p>
            <SavedIaps />
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="offline-home-root">
      <main className="offline-home-shell">
        <header className="offline-home-head">
          <div>
            <span className="offline-home-kicker">OFFLINE FIELD MODE</span>
            <h1>Line Medic</h1>
            <p>{profile?.full_name || 'Member'} · previously approved on this device</p>
          </div>
          <img src={FIRELINE_MEDIC_BADGE} alt="Line Medic" />
        </header>

        <div className="offline-home-note">
          No service. Offline access is intentionally limited to checked-out gear, ICS 214 documentation, field references, Saved IAPs, the Drug Calculator, and the last saved Fire Weather forecast. Supported local changes remain on this device until service returns.
        </div>

        <div className="offline-home-grid">
          <button type="button" className="offline-home-tile primary" onClick={() => window.location.assign('/my-gear')}>
            <span>MY GEAR</span>
            <strong>Record supplies used</strong>
            <small>Open checked-out bags and mark items used. Deploy and Return require service.</small>
          </button>
          <button type="button" className="offline-home-tile primary" onClick={() => setReference('ics214')}>
            <span>FIELD DOCUMENTATION</span>
            <strong>ICS 214</strong>
            <small>Continue saved 214s, add activities, and generate the PDF completely offline.</small>
          </button>
          <button type="button" className="offline-home-tile" onClick={() => setReference('radio')}>
            <span>FIELD REFERENCE</span>
            <strong>Radio Reference</strong>
            <small>Search the locally stored radio bank and channel guide.</small>
          </button>
          <button type="button" className="offline-home-tile" onClick={() => setReference('protocols')}>
            <span>FIELD REFERENCE</span>
            <strong>EMS Protocols</strong>
            <small>Search the configured EMS protocol set saved locally for field use.</small>
          </button>
          <button type="button" className="offline-home-tile" onClick={() => setReference('iaps')}>
            <span>FIELD REFERENCE</span>
            <strong>Saved IAPs</strong>
            <small>Open and search incident IAPs already saved on this device.</small>
          </button>
          <button type="button" className="offline-home-tile" onClick={() => setReference('drug-calculator')}>
            <span>MEDICATION CALCULATOR</span>
            <strong>Drug Calculator</strong>
            <small>Run configured dose and draw-volume calculations completely offline.</small>
          </button>
          <button type="button" className="offline-home-tile" onClick={() => setReference('fire-weather')}>
            <span>FIRE WEATHER</span>
            <strong>Last Saved Forecast</strong>
            <small>Open the most recent NWS fire-weather grid cached on this device.</small>
          </button>
        </div>

        <p className="offline-home-foot">The full dashboard returns automatically when connectivity is restored.</p>
      </main>
    </div>
  );
}
